import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Search, Loader2, Sparkles, Target, Zap, Wand2, Lightbulb, Rocket, Shuffle, Brain, Route, TrendingUp, Globe, X, Network, Share2, Star, Trash2, ShieldCheck, ShieldAlert, Mail } from "lucide-react";
import { ProspectingTools } from "../components/ProspectingTools";

const ContactCell = ({ o, onEnriched }) => {
  const [busy, setBusy] = useState(false);
  const enrich = async () => {
    setBusy(true);
    try {
      const r = await api.enrichOpp(o.id);
      if (r.found) { onEnriched(r); toast.success(`Found a verified email (${r.contact_confidence ?? "?"}% confidence).`); }
      else { onEnriched({ contact_status: "no_verified_email" }); toast.message("No verified email found for this domain."); }
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't enrich — is a website on this prospect?"); }
    finally { setBusy(false); }
  };
  if (o.contact_email) {
    return (
      <span data-testid={`contact-email-${o.id}`} className="flex items-center gap-1 text-emerald-300">
        <Mail size={11} /> {o.contact_email}{o.contact_confidence ? ` · ${o.contact_confidence}%` : ""}
      </span>
    );
  }
  return (
    <button data-testid={`enrich-${o.id}`} onClick={enrich} disabled={busy}
      className="flex items-center gap-1 text-amber-400/90 hover:text-amber-300 transition-colors disabled:opacity-50">
      {busy ? <Loader2 size={11} className="animate-spin" /> : <Mail size={11} />} Find verified email (Hunter)
    </button>
  );
};

const EntityBadge = ({ o, onVerified }) => {
  const [busy, setBusy] = useState(false);
  const verify = async () => {
    setBusy(true);
    try {
      const r = await api.verifyEntity(o.id);
      onVerified({ company_type: r.company_type, ch_verified: true, ch_found: r.ch_found, ch_number: r.ch_number });
      toast.success(r.verdict);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't verify — is Companies House connected?");
    } finally { setBusy(false); }
  };
  if (o.ch_verified) {
    const corporate = o.company_type === "ltd" || o.company_type === "llp";
    return (
      <span data-testid={`entity-verified-${o.id}`} title={o.ch_number ? `Companies House ${o.ch_number}` : "Not on the register"}
        className={`flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${corporate ? "bg-emerald-500/15 border-emerald-500/30 text-emerald-300" : "bg-amber-500/15 border-amber-500/30 text-amber-300"}`}>
        {corporate ? <ShieldCheck size={10} /> : <ShieldAlert size={10} />}
        {corporate ? "Verified · emailable" : "Consent required"}
      </span>
    );
  }
  return (
    <button data-testid={`verify-entity-${o.id}`} onClick={verify} disabled={busy}
      className="flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border border-white/15 text-slate-300 hover:bg-white/[0.06] transition-all disabled:opacity-50">
      {busy ? <Loader2 size={10} className="animate-spin" /> : <ShieldCheck size={10} />} Verify (Co. House)
    </button>
  );
};

const TIER = { A: "text-emerald-400 bg-emerald-500/10 border-emerald-500/25", B: "text-blue-400 bg-blue-500/10 border-blue-500/25", C: "text-slate-400 bg-white/5 border-white/10" };
const STYPE = {
  conventional: { icon: Target, tint: "text-slate-300" },
  behavioural: { icon: Brain, tint: "text-blue-400" },
  guerrilla: { icon: Rocket, tint: "text-amber-400" },
  completely_different: { icon: Shuffle, tint: "text-emerald-400" },
};

export const Opportunities = ({ goApprove }) => {
  const [opps, setOpps] = useState([]);
  const [brief, setBrief] = useState("");
  const [hunting, setHunting] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [trends, setTrends] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [showTrends, setShowTrends] = useState(true);
  const [nodeHunting, setNodeHunting] = useState(false);
  const [clusters, setClusters] = useState(null);
  const [mapping, setMapping] = useState(false);
  const [showClusters, setShowClusters] = useState(true);

  const load = () => api.getOpps().then(setOpps);
  useEffect(() => {
    load();
    api.getTrends().then((t) => t && setTrends(t)).catch(() => {});
    api.getClusters().then((c) => c && setClusters(c)).catch(() => {});
  }, []);

  const scan = async () => {
    setScanning(true);
    try {
      const t = await api.scanTrends(brief);
      setTrends(t); setShowTrends(true);
      toast.success(`KURT scanned the live web — ${(t.signals || []).length} fresh signals to weaponise.`);
    } catch { toast.error("KURT couldn't reach the live web. Try again."); }
    finally { setScanning(false); }
  };

  const findNodes = async () => {
    setNodeHunting(true);
    try {
      const data = await api.huntNodes(brief);
      setOpps(data);
      const nodes = data.filter((o) => o.is_node);
      const reach = nodes.reduce((s, n) => s + (parseInt(n.estimated_reach) || 0), 0);
      toast.success(`KURT found ${nodes.length} multiplier nodes — reaching ~${reach} SMEs.`);
    } catch { toast.error("Node hunt failed. Try again."); }
    finally { setNodeHunting(false); }
  };

  const mapClusters = async () => {
    setMapping(true);
    try {
      const c = await api.mapClusters();
      setClusters(c); setShowClusters(true);
      toast.success(`KURT mapped ${(c.clusters || []).length} clusters — win one hub, unlock many.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Cluster mapping failed."); }
    finally { setMapping(false); }
  };

  const hunt = async () => {
    setHunting(true);
    try {
      const data = await api.source(brief);
      setOpps(data);
      toast.success(`KURT found ${data.length} fresh prospects.`);
    } catch { toast.error("KURT couldn't complete the hunt. Try again."); }
    finally { setHunting(false); }
  };

  const buildStrategies = async (id) => {
    setBusyId(id);
    try {
      const updated = await api.genStrategies(id);
      setOpps((o) => o.map((x) => (x.id === id ? updated : x)));
    } catch { toast.error("Strategy generation failed."); }
    finally { setBusyId(null); }
  };

  const chooseStrategy = async (oppId, strategyId) => {
    setBusyId(oppId + strategyId);
    try {
      await api.createAssets(oppId, strategyId);
      await load();
      toast.success("Assets created & queued through the compliance gate — see Approve.");
      goApprove();
    } catch { toast.error("Asset creation failed."); }
    finally { setBusyId(null); }
  };

  const designJourney = async (oppId, strategyId) => {
    setBusyId(oppId + strategyId + "j");
    try {
      const updated = await api.designJourney(oppId, strategyId);
      setOpps((o) => o.map((x) => (x.id === oppId ? updated : x)));
      toast.success("KURT designed a connected multi-step journey — steps queued in Approve.");
    } catch { toast.error("Journey design failed."); }
    finally { setBusyId(null); }
  };

  return (
    <div data-testid="screen-opportunities" className="max-w-5xl mx-auto">
      <div className="kz-fade-up">
        <p className="text-sm text-emerald-400 font-semibold tracking-wide">OPPORTUNITIES</p>
        <h1 className="kz-head text-3xl md:text-4xl font-extrabold tracking-tight mt-2">What KURT found — and why it can create sales.</h1>
        <p className="text-slate-400 mt-2 text-sm">Real UK prospects, scored and tiered, with the trigger that makes them worth contacting now.</p>
      </div>

      <div className="mt-6 flex flex-col sm:flex-row gap-2 kz-fade-up">
        <input data-testid="hunt-brief-input" value={brief} onChange={(e) => setBrief(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !hunting && hunt()}
          placeholder="Describe who to hunt (e.g. 'UK independent cafés that just expanded')…"
          className="flex-1 bg-[#121824] border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-emerald-500/40 transition-all" />
        <button data-testid="hunt-btn" onClick={hunt} disabled={hunting}
          className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-emerald-500 text-[#04140d] font-semibold text-sm hover:bg-emerald-400 disabled:opacity-60 transition-all hover:scale-[1.02]">
          {hunting ? <Loader2 size={17} className="animate-spin" /> : <Search size={17} />}
          {hunting ? "Hunting…" : "Hunt prospects"}
        </button>
        <button data-testid="scan-trends-btn" onClick={scan} disabled={scanning}
          className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl border border-emerald-500/30 text-emerald-300 font-semibold text-sm hover:bg-emerald-500/10 disabled:opacity-60 transition-all">
          {scanning ? <Loader2 size={17} className="animate-spin" /> : <Globe size={17} />}
          {scanning ? "Scanning web…" : "Scan live trends"}
        </button>
      </div>

      <div className="mt-2 flex flex-col sm:flex-row gap-2 kz-fade-up">
        <button data-testid="find-nodes-btn" onClick={findNodes} disabled={nodeHunting}
          className="flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-xl border border-amber-500/30 text-amber-300 font-semibold text-sm hover:bg-amber-500/10 disabled:opacity-60 transition-all">
          {nodeHunting ? <Loader2 size={17} className="animate-spin" /> : <Network size={17} />}
          {nodeHunting ? "Hunting multipliers…" : "Find multipliers (one node → many members)"}
        </button>
        <button data-testid="map-clusters-btn" onClick={mapClusters} disabled={mapping}
          className="flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-xl border border-blue-500/30 text-blue-300 font-semibold text-sm hover:bg-blue-500/10 disabled:opacity-60 transition-all">
          {mapping ? <Loader2 size={17} className="animate-spin" /> : <Share2 size={17} />}
          {mapping ? "Mapping clusters…" : "Map clusters"}
        </button>
      </div>

      <ProspectingTools onMapsDone={() => api.getOpps().then(setOpps)} />

      {clusters && showClusters && (clusters.clusters || []).length > 0 && (
        <div data-testid="clusters-panel" className="mt-5 rounded-2xl border border-blue-500/20 bg-blue-500/[0.04] p-5 kz-fade-up">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <Share2 size={16} className="text-blue-400" />
              <div>
                <p className="text-[10px] uppercase tracking-wider text-blue-400 font-bold">Cluster intelligence</p>
                <p className="kz-head text-sm font-bold text-slate-100 mt-0.5">{clusters.summary}</p>
              </div>
            </div>
            <button data-testid="close-clusters-btn" onClick={() => setShowClusters(false)} className="text-slate-500 hover:text-slate-300 transition-colors"><X size={16} /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-2 mt-4">
            {clusters.clusters.map((cl, i) => (
              <div key={i} data-testid={`cluster-${i}`} className="rounded-xl bg-[#0B0F17] border border-white/[0.06] p-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-bold text-slate-100">{cl.name}</p>
                  <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full ${cl.priority === "high" ? "bg-emerald-500/15 text-emerald-400" : cl.priority === "medium" ? "bg-amber-500/15 text-amber-400" : "bg-white/5 text-slate-400"}`}>{cl.priority}</span>
                </div>
                <p className="text-[11px] text-blue-300 mt-1.5 flex items-center gap-1"><Network size={11} /> Win first: <b className="text-slate-200">{cl.hub}</b> <span className="text-slate-500">· {cl.hub_type}</span></p>
                {cl.estimated_members > 0 && <p className="text-[10px] text-slate-500 mt-0.5">unlocks ~{cl.estimated_members} members</p>}
                <p className="text-[11px] text-slate-400 mt-1.5 leading-relaxed">{cl.unlock_play}</p>
                {(cl.members || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {cl.members.slice(0, 6).map((m, j) => <span key={j} className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/5 text-slate-400">{m}</span>)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {trends && showTrends && (
        <div data-testid="trends-panel" className="mt-5 rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.04] p-5 kz-fade-up">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <TrendingUp size={16} className="text-emerald-400" />
              <div>
                <p className="text-[10px] uppercase tracking-wider text-emerald-400 font-bold">Live trend scan</p>
                <p className="kz-head text-sm font-bold text-slate-100 mt-0.5">{trends.headline}</p>
              </div>
            </div>
            <button data-testid="close-trends-btn" onClick={() => setShowTrends(false)} className="text-slate-500 hover:text-slate-300 transition-colors"><X size={16} /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-2 mt-4">
            {(trends.signals || []).map((s, i) => (
              <div key={i} data-testid={`trend-signal-${i}`} className="rounded-xl bg-[#0B0F17] border border-white/[0.06] p-3">
                <p className="text-xs font-bold text-slate-100">{s.title}</p>
                <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">{s.insight}</p>
                <div className="flex flex-wrap items-center gap-2 mt-2 text-[10px]">
                  {s.brand_or_source && <span className="px-1.5 py-0.5 rounded-full bg-white/5 text-slate-400">{s.brand_or_source}</span>}
                  {s.tactic && <span className="text-emerald-400/90">{s.tactic}</span>}
                </div>
                {s.why_it_converts && <p className="text-[10px] text-slate-500 mt-1.5 italic">Why it converts: {s.why_it_converts}</p>}
              </div>
            ))}
          </div>
          {(trends.apply_now || []).length > 0 && (
            <div className="mt-4">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">KURT will apply</p>
              <ul className="space-y-1">
                {trends.apply_now.map((a, i) => (
                  <li key={i} className="flex gap-1.5 text-xs text-slate-300"><Sparkles size={11} className="text-emerald-400 mt-0.5 shrink-0" /><span>{a}</span></li>
                ))}
              </ul>
            </div>
          )}
          <p className="text-[10px] text-emerald-400/70 mt-3">These signals now feed every new strategy KURT designs.</p>
        </div>
      )}

      <div className="mt-8 space-y-4">
        {opps.length > 0 && (
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-500">{opps.length} prospect{opps.length === 1 ? "" : "s"}</p>
            <button data-testid="clear-opps-btn" onClick={async () => { if (window.confirm("Clear all prospects?")) { await api.clearOpps(); load(); toast.success("Cleared."); } }}
              className="flex items-center gap-1.5 text-[11px] font-semibold text-red-400/80 hover:text-red-400 transition-colors"><Trash2 size={12} /> Clear all</button>
          </div>
        )}
        {opps.length === 0 && !hunting && (
          <div className="rounded-2xl border border-white/10 bg-[#121824] p-10 text-center kz-fade-up">
            <Target size={26} className="text-emerald-400 mx-auto" />
            <p className="kz-head text-lg font-bold mt-3">No prospects yet.</p>
            <p className="text-slate-400 text-sm mt-1">Tell KURT who to find, and he'll go hunt them.</p>
          </div>
        )}
        {opps.map((o) => (
          <div key={o.id} data-testid={`opportunity-${o.id}`} className="kz-fade-up rounded-2xl border border-white/10 bg-[#121824] p-6 hover:border-white/20 transition-all">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="kz-head text-lg font-bold">{o.company_name}</h3>
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${TIER[o.tier] || TIER.C}`}>Tier {o.tier}</span>
                  <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-slate-400">{o.company_type}</span>
                  <EntityBadge o={o} onVerified={(upd) => setOpps((p) => p.map((x) => x.id === o.id ? { ...x, ...upd } : x))} />
                  {o.is_node && (
                    <span data-testid={`node-badge-${o.id}`} className="flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-300">
                      <Network size={10} /> Multiplier{o.estimated_reach ? ` · ~${o.estimated_reach} SMEs` : ""}
                    </span>
                  )}
                  {o.kind === "maps" && (
                    <span data-testid={`maps-badge-${o.id}`} className={`flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${o.data_source === "google_places" ? "bg-teal-500/15 border-teal-500/30 text-teal-300" : "bg-white/5 border-white/10 text-slate-400"}`}>
                      <Star size={10} /> Maps{o.data_source === "google_places" ? " · live" : " · demo"}
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-400 mt-1">{o.sector} · {o.location}</p>
                {o.kind === "maps" && (o.rating || o.website) && (
                  <p className="text-xs text-slate-500 mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                    {o.rating ? <span className="flex items-center gap-1"><Star size={11} className="text-amber-400" /> {o.rating} ({o.review_count || 0})</span> : null}
                    {o.website ? <a href={o.website.startsWith("http") ? o.website : `https://${o.website}`} target="_blank" rel="noreferrer" className="text-teal-400 hover:underline truncate max-w-[200px]">{o.website}</a> : null}
                    {o.s_me_fit ? <span className="text-slate-500 italic">{o.s_me_fit}</span> : null}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <div className="text-right">
                  <div className="kz-head text-2xl font-extrabold text-emerald-400">{o.conversion_value ?? o.score}</div>
                  <div className="text-[9px] text-slate-500 uppercase tracking-wider">conversion</div>
                </div>
                <div className="w-px h-8 bg-white/10" />
                <div className="text-right">
                  <div className={`kz-head text-2xl font-extrabold ${(o.multiplier_value || 0) >= 60 ? "text-amber-400" : "text-slate-300"}`}>{o.multiplier_value ?? 0}</div>
                  <div className="text-[9px] text-slate-500 uppercase tracking-wider">multiplier</div>
                </div>
                <button data-testid={`del-opp-${o.id}`} onClick={async () => { await api.deleteOpp(o.id); setOpps((p) => p.filter((x) => x.id !== o.id)); }}
                  className="text-slate-600 hover:text-red-400 transition-colors ml-1" title="Remove"><Trash2 size={15} /></button>
              </div>
            </div>

            {o.is_node && o.node_angle && (
              <div className="mt-3 rounded-xl bg-amber-500/[0.06] border border-amber-500/20 p-3">
                <div className="flex items-center gap-1.5 text-amber-300 text-xs font-semibold mb-1"><Network size={12} /> How one node unlocks many</div>
                <p className="text-sm text-slate-300 leading-relaxed">{o.node_angle || o.angle_to_win}</p>
                {o.network_description && <p className="text-xs text-slate-500 mt-1">{o.network_description}</p>}
              </div>
            )}

            <div className="mt-3 rounded-xl bg-[#0B0F17] border border-white/[0.06] p-3">
              <div className="flex items-center gap-1.5 text-amber-400 text-xs font-semibold mb-1"><Zap size={12} /> Trigger event</div>
              <p className="text-sm text-slate-300 leading-relaxed">{o.trigger_event}</p>
            </div>

            {o.factors && (
              <div className="flex flex-wrap gap-1.5 mt-3" data-testid={`factors-${o.id}`}>
                {Object.entries(o.factors)
                  .sort((a, b) => (b[1] || 0) - (a[1] || 0))
                  .slice(0, 5)
                  .map(([k, v]) => (
                    <span key={k} className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-slate-400">
                      {k.replace(/_/g, " ")} <b className="text-emerald-400">{v}</b>
                    </span>
                  ))}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-3 text-xs text-slate-400">
              <span><b className="text-slate-300">Target:</b> {o.decision_maker_role}</span>
              <span><b className="text-slate-300">Source:</b> {o.source}</span>
              <ContactCell o={o} onEnriched={(upd) => setOpps((p) => p.map((x) => x.id === o.id ? { ...x, ...upd } : x))} />
            </div>

            {(!o.strategies || o.strategies.length === 0) ? (
              <button data-testid={`build-strategies-${o.id}`} onClick={() => buildStrategies(o.id)} disabled={busyId === o.id}
                className="mt-4 flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/[0.06] text-slate-100 font-semibold text-sm hover:bg-white/[0.1] disabled:opacity-60 transition-all">
                {busyId === o.id ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} className="text-emerald-400" />}
                {busyId === o.id ? "KURT is thinking up 4 plays…" : "Build 4 strategies"}
              </button>
            ) : (
              <div className="mt-4">
                <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-1.5"><Lightbulb size={13} className="text-emerald-400" /> Pick a strategy to create assets</p>
                <div className="grid sm:grid-cols-2 gap-2">
                  {o.strategies.map((s) => {
                    const meta = STYPE[s.type] || STYPE.conventional;
                    const Icon = meta.icon;
                    const bAsset = busyId === (o.id + s.id);
                    const bJny = busyId === (o.id + s.id + "j");
                    return (
                      <div key={s.id} data-testid={`strategy-${s.id}`} className="rounded-xl border border-white/10 bg-[#0B0F17] p-4 hover:border-emerald-500/30 transition-all">
                        <span className={`flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider ${meta.tint}`}><Icon size={13} />{s.type.replace("_", " ")}</span>
                        <h4 className="kz-head font-bold text-slate-50 mt-2 leading-snug">{s.name}</h4>
                        <p className="text-xs text-slate-400 mt-1.5 leading-relaxed line-clamp-3">{s.idea}</p>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {(s.psychology || []).slice(0, 4).map((p) => (
                            <span key={p} className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/5 text-slate-400">{p}</span>
                          ))}
                        </div>
                        {s.borrowed_from && (
                          <p data-testid={`borrowed-${s.id}`} className="text-[10px] text-amber-400/90 mt-2 flex gap-1"><Sparkles size={9} className="mt-0.5 shrink-0" /><span>Borrows: {s.borrowed_from}</span></p>
                        )}
                        {s.why_it_converts && (
                          <p className="text-[10px] text-slate-500 mt-1 italic leading-snug">{s.why_it_converts}</p>
                        )}
                        <div className="text-[10px] text-slate-500 mt-2 mb-3"><Sparkles size={9} className="inline text-emerald-400" /> {s.channel}</div>
                        <div className="flex gap-1.5">
                          <button data-testid={`create-assets-${s.id}`} onClick={() => chooseStrategy(o.id, s.id)} disabled={bAsset || bJny}
                            className="flex-1 flex items-center justify-center gap-1.5 text-[11px] font-semibold px-2 py-2 rounded-lg bg-emerald-500 text-[#04140d] hover:bg-emerald-400 disabled:opacity-60 transition-all">
                            {bAsset ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Single play
                          </button>
                          <button data-testid={`journey-${s.id}`} onClick={() => designJourney(o.id, s.id)} disabled={bAsset || bJny}
                            className="flex-1 flex items-center justify-center gap-1.5 text-[11px] font-semibold px-2 py-2 rounded-lg border border-white/12 text-slate-200 hover:bg-white/[0.06] disabled:opacity-60 transition-all">
                            {bJny ? <Loader2 size={12} className="animate-spin" /> : <Route size={12} className="text-emerald-400" />} Full journey
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {o.journey && (
                  <div className="mt-4 rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04] p-4" data-testid={`journey-plan-${o.id}`}>
                    <div className="flex items-center gap-2 mb-3"><Route size={14} className="text-emerald-400" /><span className="text-sm font-bold text-slate-100">{o.journey.name}</span><span className="text-[10px] text-slate-500">· connected journey</span></div>
                    <div className="space-y-0">
                      {(o.journey.steps || []).map((st, i) => (
                        <div key={i} className="flex gap-3 pb-3 last:pb-0">
                          <div className="flex flex-col items-center">
                            <span className="w-6 h-6 rounded-full bg-emerald-500/15 text-emerald-400 text-[11px] font-bold flex items-center justify-center shrink-0">{st.n}</span>
                            {i < o.journey.steps.length - 1 && <span className="w-px flex-1 bg-white/10 my-1" />}
                          </div>
                          <div className="pb-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-semibold text-slate-200">{st.channel}</span>
                              <span className="text-[10px] text-slate-500 kz-mono">{st.timing}</span>
                              {st.needs_hands && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-blue-500/15 text-blue-300">your hands</span>}
                            </div>
                            <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">{st.purpose}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-[11px] text-emerald-400/80 mt-1">Each step is queued as its own action in Approve, through the compliance gate.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
